"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import { Checkbox, Notice, TextField } from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { KeyedWorking } from "@/components/tools/ciphers-polygraphic/keyed-working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  foreignLetters,
  KEEP_ALL_TEXT,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";
import { formatCipherOutput } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";
import {
  describePorta,
  portaRow,
  portaRowLabel,
  portaSquare,
  portaTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/porta";

const STEP_LIMIT = 20;
const LETTERS = [...ALPHABET];
const ROW_LABELS = Array.from({ length: 13 }, (_, row) => portaRowLabel(row));

/** English strings. A translated content file overrides these through `ui`. */
export const PORTA_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Meet me at the old stone bridge",
  keyword: "Keyword",
  keywordSample: "PORTA",
  keywordHint:
    "Letters only. The keyword repeats along the message, one key letter for each letter of text. A and B pick the same alphabet, so do C and D, and so on.",
  keywordMissing: "Enter a keyword with at least one letter to see the result.",
  reciprocal:
    "Porta is reciprocal: the same steps encode and decode, so Decode gives the same answer as Encode.",
  group: "Cipher text in groups of five (letters only, in capitals)",
  keep: "Keep in the result",
  keepCase: "Lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  foreign:
    "one: {count} letter outside A–Z was left unchanged: {letters}.|other: {count} letters outside A–Z were left unchanged: {letters}.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  working: "Working",
  numbering:
    "Letters are numbered A = 0, B = 1 … Z = 25, and every sum wraps around at 26.",
  ruleHalves:
    "Each alphabet swaps the two halves of the alphabet: a letter from A–M becomes a letter from N–Z, and the other way round. That is why the cipher is its own inverse.",
  ruleFirstHalf:
    "Plain letter A–M (0–12): cipher letter = 13 + ((letter + pair) mod 13).",
  ruleSecondHalf:
    "Plain letter N–Z (13–25): cipher letter = (letter − 13 − pair) mod 13.",
  rulePair:
    "The pair number is the key letter's number divided by two, rounded down: A and B give 0, C and D give 1 … Y and Z give 12.",
  squareLabel: "Porta square — 13 alphabets",
  squareHint:
    "Row = pair of key letters, column = plain letter, cell = cipher letter. Select a letter below to light it up: its row and column turn blue and the cell where they meet turns amber.",
  plainLetter: "Plain letter",
  cipherLetter: "Cipher letter",
  keyLetter: "Key letter",
  alphabetColumn: "Alphabet",
  arithmetic: "Arithmetic",
  showLetter: "Show {letter} in the square",
  stepLimit:
    "one: Showing the first letter.|other: Showing the first {count} letters.",
} as const;

export default function PortaCipherWidget() {
  const strings = useToolUi(PORTA_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [options, setOptions] = useState(KEEP_ALL_TEXT);
  const [group, setGroup] = useState(false);

  const result = portaTransform(input, keyword, mode);
  const encoding = mode === "encode";
  const output = formatCipherOutput(result.output, options, group && encoding);
  const foreign = foreignLetters(input);
  const shown = result.steps.slice(0, STEP_LIMIT);

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={strings.keywordHint}
        id="po-keyword"
        label={strings.keyword}
        mono
        onChange={setKeyword}
        value={keyword}
      />
      <p className={HINT}>{strings.reciprocal}</p>
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const working =
    result.key === "" ? (
      <Notice>{strings.keywordMissing}</Notice>
    ) : (
      <KeyedWorking
        head={[
          encoding ? strings.plainLetter : strings.cipherLetter,
          strings.keyLetter,
          strings.alphabetColumn,
          strings.arithmetic,
          encoding ? strings.cipherLetter : strings.plainLetter,
        ]}
        limitNote={
          result.steps.length > STEP_LIMIT
            ? tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                count: fmt(STEP_LIMIT),
              })
            : undefined
        }
        marks={shown.map((step) => ({
          row: portaRow(letterIndex(step.key)),
          column: letterIndex(step.plain),
        }))}
        rows={shown.map((step) => [
          encoding ? step.plain : step.cipher,
          step.key,
          portaRowLabel(portaRow(letterIndex(step.key))),
          describePorta(
            letterIndex(step.key),
            letterIndex(encoding ? step.plain : step.cipher)
          ),
          encoding ? step.cipher : step.plain,
        ])}
        rules={[
          strings.numbering,
          strings.ruleHalves,
          strings.ruleFirstHalf,
          strings.ruleSecondHalf,
          strings.rulePair,
        ]}
        selectLabel={strings.showLetter}
        square={{
          cells: portaSquare(),
          columns: LETTERS.length,
          label: strings.squareLabel,
          hint: strings.squareHint,
          rowHeaders: ROW_LABELS,
          columnHeaders: LETTERS,
        }}
      />
    );

  return (
    <CipherWorkbench
      idPrefix="po"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        foreign.count > 0 ? (
          <Notice>
            {tPlural(strings.foreign, locale, foreign.count, {
              count: fmt(foreign.count),
              letters: foreign.letters.join(" "),
            })}
          </Notice>
        ) : null
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={
        group && encoding
          ? undefined
          : {
              value: options,
              onChange: setOptions,
              show: ["keepCase", "keepSpaces", "keepPunctuation"],
            }
      }
      working={working}
    />
  );
}
